import { useEffect, useMemo, useRef, useState } from 'react';
import { freshCanvas } from '../scene/useScene';
import ViewControls from '../scene/ViewControls';
import { Link } from 'react-router-dom';
import RowMenu from '../../components/RowMenu';
import { KINDS } from '../../concepts/kinds';
import { isKnownKind, type ProgramTrack, type ProgramView } from '../../programs/types';
import { AddProgramsMenu } from '../../pages/home/shared';
import { ProgramMenuItems } from '../../pages/home/ClassicHome';
import type { HomeModel } from '../../pages/home/useHome';
import type { ExpeditionHandle, SiteSpec } from './Expedition';

const COLOURS = ['#c2462f', '#2f7fb8', '#3f9a57', '#8a4fb0', '#e09a2a', '#1f9b95', '#b8456f', '#5b6bd6'];
const SHAPES: SiteSpec['shape'][] = ['house', 'tower', 'tent', 'dome'];

function hash(s: string) {
  let h = 0;
  for (const ch of s) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h;
}

function motionAllowed(reduceMotion: boolean) {
  return !reduceMotion && window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
}

export default function ExpeditionHome({ h }: { h: HomeModel }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const handleRef = useRef<ExpeditionHandle | null>(null);
  const [failed, setFailed] = useState(false);
  const [track, setTrack] = useState<ProgramTrack>('CURRENT');
  const [region, setRegion] = useState<number | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [hover, setHover] = useState<{ id: string; x: number; y: number } | null>(null);
  const moving = motionAllowed(h.settings.reduceMotion);

  const regions = h.categories.length ? h.categories : ['Programs'];
  const regionOf = (p: ProgramView) => Math.max(0, regions.indexOf(p.category ?? ''));
  const all = h.programs.filter((p) => p.track === track);
  const list = region === null ? all : all.filter((p) => regionOf(p) === region);

  const sites: SiteSpec[] = useMemo(() => all.map((p) => ({
    id: p.id,
    colour: COLOURS[hash(p.category ?? p.kind) % COLOURS.length],
    shape: SHAPES[hash(p.kind) % SHAPES.length],
    region: regionOf(p),
    due: h.dueIds.has(p.id),
  })), [all.map((p) => p.id).join(), h.dueIds, regions.join()]);

  const latest = useRef({ sites, selected, region });
  latest.current = { sites, selected, region };

  useEffect(() => {
    const placeholder = canvasRef.current;
    if (!placeholder || failed) return;
    let cancelled = false;
    const root = document.documentElement;
    const { canvas, restore } = freshCanvas(placeholder);
    void import('./Expedition').then(({ mountExpedition }) => {
      if (cancelled) return;
      try {
        handleRef.current = mountExpedition(canvas, motionAllowed(h.settings.reduceMotion), {
          onPick: (id) => { setRegion(null); setSelected(id); },
          onHover: (id, x, y) => setHover(id ? { id, x, y } : null),
          onLost: () => { if (!cancelled) setFailed(true); },
        });
        handleRef.current.setSites(latest.current.sites);
        handleRef.current.setSelected(latest.current.selected);
        handleRef.current.setRegion(latest.current.region);
        root.setAttribute('data-expedition', 'on');
      } catch {
        setFailed(true);
      }
    });
    return () => {
      cancelled = true;
      handleRef.current?.dispose();
      handleRef.current = null;
      restore();
      root.removeAttribute('data-expedition');
    };
  }, [failed]);

  useEffect(() => { handleRef.current?.setSites(sites); }, [sites]);
  useEffect(() => { handleRef.current?.setSelected(selected); }, [selected]);
  useEffect(() => { handleRef.current?.setRegion(region); }, [region]);
  useEffect(() => { handleRef.current?.setMoving(moving); }, [moving]);
  useEffect(() => { setSelected(null); setRegion(null); }, [track]);

  const chosen = list.find((p) => p.id === selected) ?? null;
  const at = chosen ? list.indexOf(chosen) : -1;
  const step = (d: number) => {
    if (!list.length) return;
    const next = at < 0 ? (d > 0 ? 0 : list.length - 1) : (at + d + list.length) % list.length;
    setSelected(list[next].id);
  };
  const playUrl = (p: ProgramView) => `/play/${p.id}${h.learnerId ? `?learner=${encodeURIComponent(h.learnerId)}` : ''}`;
  const kindLabel = (p: ProgramView) => (isKnownKind(p.kind) ? KINDS[p.kind].label : p.kind);
  const hovered = hover ? all.find((p) => p.id === hover.id) : null;

  return (
    <div className="exp">
      <section className="exp-panel" aria-label="Programs">
        <div className="exp-tabs" role="tablist" aria-label="Which programs">
          {(['CURRENT', 'MAINTENANCE'] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={track === t}
              className={track === t ? 'exp-tab is-active' : 'exp-tab'}
              onClick={() => setTrack(t)}
            >
              {t === 'CURRENT' ? 'Learning now' : 'Learned'}
              <span className="exp-count">{h.programs.filter((p) => p.track === t).length}</span>
            </button>
          ))}
        </div>

        {h.error && <div className="error" role="alert">{h.error}</div>}

        {chosen ? (
          <article className="exp-entry">
            <p className="exp-kicker">{chosen.category ?? 'Program'} · {at + 1} of {list.length}</p>
            <h1 className="exp-title">{chosen.name}</h1>
            <p className="exp-body">
              {kindLabel(chosen)}
              {h.dueIds.has(chosen.id) ? ' — ready for a check.' : '.'}
            </p>
            <div className="exp-entry-actions">
              <Link className="btn" to={playUrl(chosen)}>Play</Link>
              <RowMenu label={`More for ${chosen.name}`}>
                <ProgramMenuItems h={h} p={chosen} />
              </RowMenu>
              <button type="button" className="btn secondary" onClick={() => setSelected(null)}>Back to the map</button>
            </div>
          </article>
        ) : (
          <article className="exp-entry">
            <p className="exp-kicker">{region === null ? 'The whole island' : regions[region]}</p>
            <h1 className="exp-title">
              {h.loading ? 'Loading…' : all.length === 0
                ? (track === 'CURRENT' ? 'No programs yet' : 'Nothing learned yet')
                : 'Choose a place to visit'}
            </h1>
            <p className="exp-body">
              {all.length === 0
                ? (track === 'CURRENT' ? 'Add a program and it appears on the island.' : 'Programs move here once they are learned.')
                : `${list.length} ${list.length === 1 ? 'program' : 'programs'}. Pick one from the list or tap a light on the island.`}
            </p>
          </article>
        )}

        {list.length > 0 && (
          <ul className="exp-list">
            {list.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className={selected === p.id ? 'exp-item is-active' : 'exp-item'}
                  aria-pressed={selected === p.id}
                  onClick={() => setSelected(p.id)}
                >
                  <span className="exp-item-dot" style={{ background: COLOURS[hash(p.category ?? p.kind) % COLOURS.length] }} aria-hidden="true" />
                  <span className="exp-item-name">{p.name}</span>
                  {h.dueIds.has(p.id) && <span className="exp-item-due">check</span>}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="exp-foot">
          <div className="exp-steps">
            <button type="button" className="exp-step" aria-label="Previous program" disabled={!list.length} onClick={() => step(-1)}>‹</button>
            <button type="button" className="exp-step" aria-label="Next program" disabled={!list.length} onClick={() => step(1)}>›</button>
          </div>
          <div className="exp-actions">
            <AddProgramsMenu h={h} />
          </div>
        </div>
      </section>

      <section className="exp-stage" aria-label="Island map">
        {!failed && <canvas ref={canvasRef} className="exp-canvas" aria-hidden="true" />}
        {!failed && <ViewControls handleRef={handleRef} inline />}
        {failed && <p className="exp-fallback">The map could not be drawn on this device. Every program is in the list.</p>}
        {hovered && hover && (
          <div className="exp-tip" style={{ left: hover.x, top: hover.y }} aria-hidden="true">{hovered.name}</div>
        )}
        {regions.length > 1 && (
          <nav className="exp-scenes" aria-label="Areas">
            <button
              type="button"
              className={region === null ? 'exp-scene is-active' : 'exp-scene'}
              aria-pressed={region === null}
              onClick={() => { setRegion(null); setSelected(null); }}
            >
              All
            </button>
            {regions.map((r, i) => (
              <button
                key={r}
                type="button"
                className={region === i ? 'exp-scene is-active' : 'exp-scene'}
                aria-pressed={region === i}
                title={r}
                onClick={() => { setRegion(i); setSelected(null); }}
              >
                <span className="exp-scene-n">{String(i + 1).padStart(2, '0')}</span>
                <span className="exp-scene-name">{r}</span>
              </button>
            ))}
          </nav>
        )}
      </section>
    </div>
  );
}
